import React, { useMemo, useRef, useState } from 'react';

import { observer } from 'mobx-react';

import { Dropdown } from 'semantic-ui-react';

import { debounce } from 'lodash';

import '../css/UserAssignableInstitutionDropdown.less';

import { SatCoreRegister } from '../SatCoreRegistry';

import UserService from '../services/UserService';

const MIN_SEARCH_CHARS = 3;
const SEARCH_DEBOUNCE = 800;

/**
 * Searchable dropdown of institutions that can be assigned to a user.
 *
 * `searchType` chooses the scope:
 *   - `'school'` (default): schools within the given `districtId`.
 *   - `'institution'`: any institution across the publisher.
 */
const UserAssignableInstitutionDropdown = observer((props = {}) => {
  const {
    className = '',
    disabled = false,
    districtId = null,
    existingUserInstitutions = [],
    onChange,
    searchType = 'school',
    t
  } = props;

  const translationKeySuffix = searchType === 'institution' ? 'Institution' : 'School';

  const [isSearching, setIsSearching] = useState(false);
  const [noResultsMessage, setNoResultsMessage] = useState(t(`minCharsMsg${translationKeySuffix}`));
  const [options, setOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);

  // id counter; incremented on each new search and on backspace-clear,
  // so that responses from stale searches can be identified and dropped
  const activeRequestRef = useRef(0);

  const renderInstitutionOption = (institution) => (
    <div className='user-assignable-institution-option'>
      <div className='user-assignable-institution-option-name'>{institution.name}</div>
      <div className='user-assignable-institution-option-subtitle'>
        {[institution.city, institution.stateName].filter(Boolean).join(', ')}
      </div>
    </div>
  );

  const toOption = (institution) => ({
    content: renderInstitutionOption(institution),
    institution,
    key: institution.id,
    text: institution.name,
    value: institution.id
  });

  const runSearch = async (searchText) => {
    const requestId = ++activeRequestRef.current;
    const institutions = searchType === 'school'
      ? await UserService.fetchUserAssignableSchoolInstitutions({ districtId, searchText })
      : await UserService.fetchUserAssignablePublisherInstitutions({ districtId, searchText });
    // if a newer search/clear superseded this one, drop the stale response
    if (requestId !== activeRequestRef.current) {
      return;
    }
    // exclude institutions already assigned to the user from the dropdown options
    const existingIds = new Set(existingUserInstitutions.map((institution) => institution.id));
    const filtered = (institutions || []).filter((institution) => !existingIds.has(institution.id));
    setOptions(filtered.map(toOption));
    setIsSearching(false);
    setNoResultsMessage(t(`noResultsMsg${translationKeySuffix}`));
  };

  // wait until the user has paused typing for SEARCH_DEBOUNCE ms before
  // searching. Rebuild when district or search type changes so the next
  // search uses the current value.
  const debouncedSearch = useMemo(() => debounce(runSearch, SEARCH_DEBOUNCE), [districtId, searchType]);

  const handleSearchChange = async (_event, { searchQuery: searchText }) => {
    setSearchQuery(searchText);
    if (searchText.length === 0 && selectedOption) {
      // user cleared the input -- drop the selection so the placeholder shows again
      setSelectedOption(null);
      await onChange?.(null);
    }
    if (searchText.length < MIN_SEARCH_CHARS) {
      resetSearch();
      return;
    }
    setIsSearching(true);
    debouncedSearch(searchText);
  };

  const handleChange = async (_event, { value: selectedValue }) => {
    debouncedSearch.cancel();
    setSearchQuery('');
    const option = options.find((o) => o.value === selectedValue);
    setSelectedOption(option || null);
    await onChange?.(option?.institution || null);
  };

  // when reopening with a selection, populate the search input with the selected option's
  // text and narrow the menu to just that option until the user starts editing
  const handleOpen = () => {
    if (selectedOption) {
      setSearchQuery(selectedOption.text);
      setOptions([selectedOption]);
    }
  };

  // when the dropdown closes without a selection, reset to the first-time state so the
  // next open starts fresh; if there is a selection, leave it as-is (handleOpen will
  // restore options on the next open)
  const handleClose = () => {
    if (!selectedOption) {
      setSearchQuery('');
      resetSearch();
    }
  };

  // reset the search state; used when the input drops below MIN_SEARCH_CHARS
  const resetSearch = () => {
    activeRequestRef.current++;
    debouncedSearch.cancel();
    setOptions([]);
    setIsSearching(false);
    setNoResultsMessage(t(`minCharsMsg${translationKeySuffix}`));
  };

  return (
    <Dropdown
      className={`UserAssignableInstitutionDropdown ${className}`.trim()}
      disabled={disabled}
      fluid
      loading={isSearching}
      noResultsMessage={noResultsMessage}
      onChange={handleChange}
      onClose={handleClose}
      onOpen={handleOpen}
      onSearchChange={handleSearchChange}
      options={options}
      placeholder={t(`placeholder${translationKeySuffix}`)}
      search={(searchOptions) => searchOptions}
      searchQuery={searchQuery}
      selectOnBlur={false}
      selection
      text={selectedOption?.text}
      value={selectedOption?.value || ''} />
  );
});

export default UserAssignableInstitutionDropdown;

SatCoreRegister('UserAssignableInstitutionDropdown', UserAssignableInstitutionDropdown);
